import { useState, useEffect } from 'react';
import { format, parseISO, compareAsc } from 'date-fns';
import { themeGet } from '@styled-system/theme-get';
import Flatpickr from 'react-flatpickr';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from '@radix-ui/themes';

import FlatpickrWrapper from '../../../../shared/DatePicker/FlatpickrWrapper.jsx';
import { useGetCampaignDates } from '../../hooks/useFetchCampaign';
import Text from '../../../../shared/Text';

import {
  EXTRA_DAYS_FOR_PRIZE_POST,
  ERROR_TIMER,
  CREATE,
  EDIT,
  REMINDER,
} from '../../utils/constants';

import { PostTimeInput } from './PostTimeInput.jsx';
import {
  getZonedDate,
  isTodayInZone,
  nextAvailableHour,
} from '../../utils/helpers';

const isDateInRange = ({ startDate, endDate, dateToCheck, timezone }) => {
  return (
    compareAsc(endDate, dateToCheck) >= 0 &&
    compareAsc(dateToCheck, getZonedDate(timezone)) >= 0 &&
    compareAsc(dateToCheck, startDate) >= 0
  );
};

const checkPostDate = (date, postDates) =>
  postDates.indexOf(format(date, 'yyyy-MM-dd')) !== -1;

const formatDate = date => format(date, 'yyyy-MM-dd');

export const ChangeDate = ({
  origin,
  campaignId,
  prize,
  handleCancel,
  handleSave,
  publishDate,
  postSendHour,
  postDate,
  maxReminderDate,
}) => {
  const { t } = useTranslation('posts');
  const {
    startDate: campaignStartDate,
    endDate: campaignEndDate,
    postDates,
    boardTimezone,
  } = useGetCampaignDates({ campaignId });
  const [date, setDate] = useState(publishDate);
  const [error, setError] = useState(false);
  const selectedDate = Array.isArray(date) ? date[0] : date;
  const [sendHour, setSendHour] = useState(postSendHour ?? 12);
  const { timezone } = useSelector(state => state.currentBoard);

  const isReminder = origin === REMINDER;

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(false), ERROR_TIMER);
      return () => clearTimeout(timer);
    }

    return undefined;
  });

  const reminderStartDate = () => {
    const postStartDate = new Date(postDate);
    postStartDate.setDate(postStartDate.getDate() + 2);

    return formatDate(postStartDate);
  };

  const startDate = () => {
    if (isReminder) return reminderStartDate();
    if (!prize) return campaignStartDate;

    const prizeStartDate = new Date(campaignEndDate);
    prizeStartDate.setDate(prizeStartDate.getDate() + 2);

    return formatDate(prizeStartDate);
  };

  const endDate = () => {
    if (isReminder) return formatDate(maxReminderDate);
    if (!prize) return campaignEndDate;

    const prizeEndDate = new Date(campaignEndDate);
    prizeEndDate.setDate(prizeEndDate.getDate() + EXTRA_DAYS_FOR_PRIZE_POST);

    return formatDate(prizeEndDate);
  };

  const onDayCreate = (dObj, dStr, fp, dayElem) => {
    const isCampaignDate = isDateInRange({
      startDate: parseISO(startDate()),
      endDate: parseISO(endDate()),
      dateToCheck: dayElem.dateObj,
      timezone,
    });
    const currentDayNoHours =
      isTodayInZone(timezone, dayElem.dateObj) &&
      nextAvailableHour(timezone) === null;

    const isPostDate = checkPostDate(dayElem.dateObj, postDates);

    if (isPostDate) {
      if (isReminder) return;

      dayElem.className += ' post-date';
      return;
    }

    if (isCampaignDate && !currentDayNoHours) {
      dayElem.className += ' campaign-date';
    }
  };

  const enableDates = dateObj => {
    const inRange = isDateInRange({
      startDate: parseISO(startDate()),
      endDate: parseISO(endDate()),
      dateToCheck: dateObj,
      timezone,
    });

    if (!inRange) {
      return false;
    }

    if (
      isTodayInZone(timezone, dateObj) &&
      nextAvailableHour(timezone) === null
    ) {
      return false;
    }

    return true;
  };

  const showCreateNoDateBtn = () => {
    if (origin === CREATE) return true;
    if (isReminder) return false;
    if (publishDate && postSendHour) return true;

    return false;
  };

  return (
    <Wrapper>
      {error && (
        <ErrorWrapper>
          <WarningIcon $isReminder={isReminder} />
          <ErrorText color="red" variant="small1" textAlign="left">
            {isReminder
              ? t('datePickerModal.submitError')
              : t('postModal.submitError')}
          </ErrorText>
        </ErrorWrapper>
      )}
      <CalendarWrapper $error={error}>
        <FlatpickrWrapper size={1} variant="postCalendar">
          <Flatpickr
            value={date}
            options={{
              monthSelectorType: 'static',
              inline: true,
              enable: [enableDates],
            }}
            onChange={newDate => setDate(newDate[0])}
            onDayCreate={onDayCreate}
          />
        </FlatpickrWrapper>
      </CalendarWrapper>
      <PostTimeInput
        sendHour={sendHour}
        setSendHour={setSendHour}
        boardTimezone={boardTimezone}
        date={date}
        isCurrentDay={isTodayInZone(timezone, date)}
      />
      <ButtonWrapper>
        <Button
          onClick={handleCancel}
          variant="outline"
          color="gray"
          size="3"
          style={{ flex: 1 }}
        >
          {t('datePickerModal.cancel')}
        </Button>
        <Button
          onClick={() => {
            if (!selectedDate || sendHour === null || sendHour === undefined) {
              return setError(true);
            }

            return handleSave({
              publishDate: selectedDate,
              sendHour,
            });
          }}
          size="3"
          style={{ flex: 1 }}
          disabled={date && (sendHour === null || sendHour === undefined)}
        >
          {t('datePickerModal.saveChanges')}
        </Button>
      </ButtonWrapper>
      {showCreateNoDateBtn() && (
        <NoDateNoTimeWrapper>
          <Button
            onClick={() => {
              handleSave({
                publishDate: null,
                sendHour: null,
                reminderSendHour: null,
                reminderDate: null,
              });
            }}
            variant="ghost"
            color="gray"
            size="3"
            style={{ width: '100%' }}
          >
            {origin === EDIT
              ? t('postModal.removePostAndTime')
              : t('postModal.createPostWithNoDate')}
          </Button>
        </NoDateNoTimeWrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const CalendarWrapper = styled.div`
  border: ${props =>
    props.$error ? '1px solid #ED1A3A' : '1px solid #DBDDE0'};
  border-radius: 8px;
  padding: 0 16px 16px 16px;
  max-width: 390px;
`;

const ErrorWrapper = styled.div`
  display: flex;
  margin-bottom: 18px;
  max-width: 340px;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const ErrorText = styled(Text)`
  flex: 1;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const NoDateNoTimeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const WarningIcon = styled.i.attrs({
  className: 'fa-regular fa-circle-info',
})`
  margin-right: 12px;
  margin-top: ${props => (props.$isReminder ? '0px' : '5px')};
  color: ${themeGet('colors.red')};
`;
